import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { safeStorage } from '../utils/safeStorage';
import { logger } from '../utils/logger';

/**
 * Cross-view selection persistence.
 *
 * Persists multi-select arrays keyed by `viewKey` (e.g. 'inbox', 'reading')
 * so users can navigate between views without losing their selection. Stored
 * in `localStorage` with a TTL — selections older than `STALE_MS` are dropped
 * on read so users never see a stale selection on the next session.
 *
 *   const { getSelection, setSelection, clearSelection } = useSelection();
 *
 * `useMultiSelect({ items, viewKey: 'inbox' })` opts into persistence
 * automatically; without `viewKey` the hook stays purely local.
 */
const SelectionContext = createContext(null);
const STORAGE_KEY = 'polkdesq_selections';
const STALE_MS = 15 * 60 * 1000; // 15 minutes

function readStore() {
  if (typeof window === 'undefined') return {};
  try {
    const raw = JSON.parse(safeStorage.getString(STORAGE_KEY) || '{}');
    const now = Date.now();
    const out = {};
    for (const [key, entry] of Object.entries(raw)) {
      if (entry && Array.isArray(entry.ids) && typeof entry.ts === 'number' && now - entry.ts < STALE_MS) {
        out[key] = entry;
      }
    }
    return out;
  } catch (err) {
    logger.warn('Selection store unreadable (corrupted or storage disabled), resetting:', err?.message);
    return {};
  }
}

function writeStore(store) {
  try {
    safeStorage.setString(STORAGE_KEY, JSON.stringify(store));
  } catch (err) {
    logger.warn('Selection store not persisted (quota/storage disabled):', err?.message);
  }
}

export function SelectionProvider({ children }) {
  const [store, setStore] = useState(() => readStore());
  const storeRef = useRef(store);
  storeRef.current = store;

  useEffect(() => { writeStore(store); }, [store]);

  // Sync across tabs.
  useEffect(() => {
    const handler = (e) => {
      if (e.key === STORAGE_KEY) setStore(readStore());
    };
    window.addEventListener('storage', handler);
    return () => window.removeEventListener('storage', handler);
  }, []);

  const getSelection = useCallback((viewKey) => {
    const entry = storeRef.current[viewKey];
    return entry?.ids ? new Set(entry.ids) : new Set();
  }, []);

  const setSelection = useCallback((viewKey, idSet) => {
    setStore((prev) => {
      const ids = Array.from(idSet || []);
      if (!ids.length) {
        if (!prev[viewKey]) return prev;
        const next = { ...prev };
        delete next[viewKey];
        return next;
      }
      return { ...prev, [viewKey]: { ids, ts: Date.now() } };
    });
  }, []);

  const clearSelection = useCallback((viewKey) => {
    setStore((prev) => {
      if (!prev[viewKey]) return prev;
      const next = { ...prev };
      delete next[viewKey];
      return next;
    });
  }, []);

  const clearAll = useCallback(() => setStore({}), []);

  const value = useMemo(() => ({ getSelection, setSelection, clearSelection, clearAll, store }),
    [getSelection, setSelection, clearSelection, clearAll, store]);

  return <SelectionContext.Provider value={value}>{children}</SelectionContext.Provider>;
}

export function useSelection() {
  const ctx = useContext(SelectionContext);
  if (!ctx) {
    // Defensive fallback so `useMultiSelect` without `viewKey` still works
    // when rendered outside the provider (e.g. tests, Storybook).
    return {
      getSelection: () => new Set(),
      setSelection: () => {},
      clearSelection: () => {},
      clearAll: () => {},
      store: {},
    };
  }
  return ctx;
}
